import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Barcode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventoryStock({ user }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(user);
  
  // Check permission
  if (!hasPermission('inventory_view')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view inventory.</p>
          <Button onClick={() => navigate('/production-unit')}>Go Back</Button>
        </div>
      </div>
    );
  }
  
  const [stock, setStock] = useState([]);
  const [filteredStock, setFilteredStock] = useState([]);
  
  // Search states
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCode, setSelectedCode] = useState('');
  
  // Master data states
  const [masterDataCodes, setMasterDataCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  
  // Delete authorization states
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchStock();
  }, []);

  useEffect(() => {
    // Filter stock based on search criteria
    applyFilters();
  }, [barcodeSearch, selectedCategory, selectedCode, stock]);

  useEffect(() => {
    // Fetch master data codes when category changes
    if (selectedCategory) {
      fetchMasterDataCodes();
    } else {
      setMasterDataCodes([]);
      setSelectedCode('');
    }
  }, [selectedCategory]);

  const fetchStock = async () => {
    try {
      const token = localStorage.getItem('token');
      const [stockResponse, issueTransactionsResponse] = await Promise.all([
        axios.get(`${API}/inventory/stock`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/inventory/issue-transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      console.log('=== STOCK DATA DEBUG ===');
      console.log('Raw stock data:', stockResponse.data);
      console.log('Sample stock item:', stockResponse.data[0]);
      
      // Calculate total issued quantity per item (by code + color_code + type)
      const issuedQuantities = {};
      issueTransactionsResponse.data.forEach(transaction => {
        const key = `${transaction.code}-${transaction.color_code}-${transaction.raw_good_type}`;
        if (!issuedQuantities[key]) {
          issuedQuantities[key] = 0;
        }
        issuedQuantities[key] += parseFloat(transaction.quantity_issued || 0);
      });
      
      console.log('Issued quantities by item:', issuedQuantities);
      
      // Enrich stock with LIVE color names ONLY (keep historical prices from PO) and add quantity_available
      const enrichedStock = await Promise.all(stockResponse.data.map(async (item) => {
        // Calculate quantity available first (before any API calls that might fail)
        const itemKey = `${item.code}-${item.color_code}-${item.raw_good_type}`;
        const totalIssued = issuedQuantities[itemKey] || 0;
        const quantityReceived = parseFloat(item.quantity_on_hand || 0);
        const quantityAvailable = quantityReceived - totalIssued;
        
        console.log(`Item ${item.code}-${item.color_code}:`, {
          quantityReceived,
          totalIssued,
          quantityAvailable,
          itemKey,
          raw_good_type: item.raw_good_type
        });
        
        try {
          // Determine master data type from raw_good_type
          let masterType;
          if (item.raw_good_type === 'FABRIC LN') {
            masterType = 'fabrics';
          } else if (item.raw_good_type === 'TRIMS AND ACCESSORIES LN-Z') {
            masterType = 'trims';
          } else if (item.raw_good_type === 'PACKAGING LN-Y') {
            masterType = 'packaging';
          }
          
          if (masterType) {
            const masterResponse = await axios.get(
              `${API}/master-data/by-code/${masterType}/${item.code}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (masterResponse.data) {
              const masterData = masterResponse.data;
              const currentColor = masterData.colors?.find(c => c.color_code === item.color_code);
              
              if (currentColor) {
                return {
                  ...item,
                  color_name: currentColor.color_name, // LIVE color name (price kept from PO)
                  quantity_available: quantityAvailable // Calculated: RECEIVED - issued
                };
              }
            }
          }
          
          // If no master data or not found, still add quantity_available
          return {
            ...item,
            quantity_available: quantityAvailable
          };
        } catch (error) {
          console.error('Error enriching stock item:', item.code, error);
          // IMPORTANT: Even if master data fetch fails, return item with quantity_available
          return {
            ...item,
            quantity_available: quantityAvailable
          };
        }
      }));
      
      setStock(enrichedStock);
      setFilteredStock(enrichedStock);
    } catch (error) {
      console.error('Error fetching stock:', error);
      toast.error('Failed to fetch stock');
    }
  };

  // Auto-refresh every 5 seconds (no editing state to pause for in stock view)
  useAutoRefresh(fetchStock, 5, false);

  const fetchMasterDataCodes = async () => {
    setLoadingCodes(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      switch (selectedCategory) {
        case 'FABRIC LN':
          endpoint = '/fabrics';
          break;
        case 'TRIMS AND ACCESSORIES LN-Z':
          endpoint = '/trims';
          break;
        case 'PACKAGING LN-Y':
          endpoint = '/packaging';
          break;
        default:
          return;
      }

      console.log(`Fetching master data from: ${API}${endpoint}`);
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Master data response:', response.data);

      // Extract unique codes
      let codes = [];
      if (selectedCategory === 'FABRIC LN') {
        codes = response.data.map(item => ({
          value: item.ln_code,
          label: `${item.ln_code}${item.composition ? ' - ' + item.composition : ''}`
        }));
      } else if (selectedCategory === 'TRIMS AND ACCESSORIES LN-Z') {
        codes = response.data.map(item => ({
          value: item.ln_z_code,
          label: `${item.ln_z_code} - ${item.item_name || ''}`
        }));
      } else if (selectedCategory === 'PACKAGING LN-Y') {
        codes = response.data.map(item => ({
          value: item.ln_y_code,
          label: `${item.ln_y_code} - ${item.item_name || ''}`
        }));
      }

      console.log('Extracted codes:', codes);
      setMasterDataCodes(codes);
      
      if (codes.length === 0) {
        toast.info('No codes found in this category');
      } else {
        toast.success(`Loaded ${codes.length} codes from master data`);
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to fetch codes from master data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingCodes(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...stock];

    // Barcode search (searches in code and color_code)
    if (barcodeSearch) {
      filtered = filtered.filter(item => 
        item.code.toLowerCase().includes(barcodeSearch.toLowerCase()) ||
        item.color_code.toLowerCase().includes(barcodeSearch.toLowerCase())
      );
    }

    // Category filter (filters by type immediately when selected)
    if (selectedCategory) {
      filtered = filtered.filter(item => {
        // Match based on raw_good_type or type field
        const itemType = item.raw_good_type || item.type || '';
        return itemType === selectedCategory;
      });
    }

    // Code filter (from master data dropdown - further narrows down category results)
    if (selectedCode) {
      filtered = filtered.filter(item => item.code === selectedCode);
    }

    setFilteredStock(filtered);
  };

  const handleBarcodeInput = (e) => {
    const value = e.target.value;
    setBarcodeSearch(value);
    
    // Clear master data selectors when barcode is being used
    if (value) {
      setSelectedCategory('');
      setSelectedCode('');
      setMasterDataCodes([]);
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSelectedCode('');
    
    // Clear barcode search when using master data selectors
    if (value) {
      setBarcodeSearch('');
    }
  };

  const clearFilters = () => {
    setBarcodeSearch('');
    setSelectedCategory('');
    setSelectedCode('');
    setFilteredStock(stock);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setAuthDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/inventory/stock/${itemToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Stock item deleted successfully');
      setAuthDialogOpen(false);
      setItemToDelete(null);
      fetchStock();
    } catch (error) {
      console.error('Error deleting stock item:', error);
      toast.error('Failed to delete stock item');
    }
  };

  const categoryOptions = [
    { value: 'FABRIC LN', label: 'Fabric LN' },
    { value: 'TRIMS AND ACCESSORIES LN-Z', label: 'Trims and Accessories LN-Z' },
    { value: 'PACKAGING LN-Y', label: 'Packaging LN-Y' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/inventory')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Inventory Stock
              </h1>
              <p className="text-sm text-gray-600">Current inventory levels - Scan or search items</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Search Filters */}
        <div className="bg-white border-2 border-black p-6 mb-6">
          <h2 className="text-lg font-bold mb-4 uppercase tracking-tight">Search Filters</h2>
          
          <div className="space-y-4">
            {/* Barcode Scanner Input */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                Barcode Scanner
              </Label>
              <div className="relative mt-1">
                <Barcode className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Scan item or color barcode here"
                  value={barcodeSearch}
                  onChange={handleBarcodeInput}
                  className="pl-12"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Scan barcode to search items (e.g., LN-00095-001)</p>
            </div>

            <div className="border-t border-gray-300 pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Or Search by Master Data</p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Category Dropdown */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                    1. Select Category
                  </Label>
                  <SearchableSelect
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    placeholder="Select category..."
                    className="mt-1"
                  />
                </div>

                {/* Code Dropdown (appears after category selection) */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                    2. Select Code from Master Data
                  </Label>
                  {selectedCategory ? (
                    <SearchableSelect
                      options={masterDataCodes}
                      value={selectedCode}
                      onChange={setSelectedCode}
                      placeholder={loadingCodes ? "Loading codes..." : "Select code..."}
                      disabled={loadingCodes}
                      className="mt-1"
                    />
                  ) : (
                    <div className="mt-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-400">
                      Select category first
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Clear Filters Button */}
            {(barcodeSearch || selectedCategory || selectedCode) && (
              <div className="flex justify-end pt-2">
                <Button onClick={clearFilters} variant="outline" size="sm">
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Stock Results */}
        <div className="bg-white border-2 border-black p-4 mb-4">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-600">
              Showing {filteredStock.length} of {stock.length} items
            </p>
            {(barcodeSearch || selectedCode) && (
              <p className="text-xs text-gray-500">
                {barcodeSearch && `Barcode: "${barcodeSearch}"`}
                {barcodeSearch && selectedCode && ' | '}
                {selectedCode && `Code: ${selectedCode}`}
              </p>
            )}
          </div>
        </div>

        {/* Stock Table */}
        {filteredStock.length === 0 ? (
          <div className="bg-white border-2 border-black p-12 text-center">
            <p className="text-gray-500 text-lg">
              {stock.length === 0 ? 'No stock available' : 'No items match your search'}
            </p>
            {(barcodeSearch || selectedCode) && (
              <Button onClick={clearFilters} className="mt-4" variant="outline">
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white border-2 border-black overflow-hidden">
            <table className="min-w-full" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <thead className="bg-gray-50 border-b-2 border-black">
                <tr>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Color Code</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Color Name</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Quantity Ordered</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Quantity Received</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Quantity Available</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">
                    {selectedCategory === 'FABRIC LN' ? 'Price per Meter' : 'Unit Price'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Last Updated</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStock.map(item => {
                  // Determine unit based on type
                  const unit = item.raw_good_type === 'FABRIC LN' ? 'meters' : 'units';
                  const isFabric = item.raw_good_type === 'FABRIC LN';
                  const priceDisplay = isFabric ? `$${item.unit_price}/m` : `$${item.unit_price}`;
                  const quantityOrdered = item.quantity_ordered || 0;
                  const quantityReceived = item.quantity_on_hand || 0;
                  const quantityAvailable = item.quantity_available || 0;
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs">{item.raw_good_type}</td>
                      <td className="px-4 py-3 text-xs">{item.code}</td>
                      <td className="px-4 py-3 text-xs">{item.color_code}</td>
                      <td className="px-4 py-3 text-xs">{item.color_name}</td>
                      <td className="px-4 py-3 text-xs">{quantityOrdered} {unit}</td>
                      <td className="px-4 py-3 text-xs">{quantityReceived} {unit}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className={`font-semibold ${quantityAvailable <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {quantityAvailable.toFixed(2)} {unit}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">{priceDisplay}</td>
                      <td className="px-4 py-3 text-xs">{new Date(item.last_updated).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteClick(item)}
                          className="text-black border-2 border-black hover:bg-gray-100"
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Authorization Dialog */}
        <AuthorizationDialog
          open={authDialogOpen}
          onOpenChange={setAuthDialogOpen}
          onAuthorized={handleDeleteConfirm}
          title="Delete Stock Item"
          message={itemToDelete ? `Enter authorization code to delete stock item ${itemToDelete.code} - ${itemToDelete.color_code}` : 'Enter authorization code to proceed'}
        />
      </main>
    </div>
  );
}
